(function () {
  'use strict';

  const rules = window.JeremiasDwFuRules;
  if (!rules) return;

  const STEPS = ['System & Einsatz', 'Nennweite', 'Höhe', 'Befestigung', 'Gebäudeverlauf', 'Zusammenfassung'];

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    })[character]);
  }

  function asNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
  }

  class JeremiasDwFuConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.catalog = this.readJson('[data-dwfu-catalog]');
      this.settings = this.readJson('[data-dwfu-settings]');
      this.app = this.querySelector('[data-dwfu-app]');
      const initialState = rules.defaultState();
      const initialDn = Number(this.settings.initialDn);
      if (rules.PRIMARY_DNS.includes(initialDn)) initialState.dn = initialDn;
      const initialHeightMm = Number(this.settings.initialHeightMm);
      if (rules.getHeightRecipe(initialHeightMm)) initialState.targetHeightMm = initialHeightMm;
      this.state = {
        ...initialState,
        anchorVerified: true,
        chimneySweepConfirmed: true
      };
      this.step = 0;
      this.returnToSummary = false;
      this.partsOpen = false;
      this.submitting = false;
      this.cartMessage = '';
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('toggle', (event) => {
        if (event.target.matches?.('.chimney-configurator__parts')) {
          this.partsOpen = event.target.open;
          const summary = event.target.querySelector('summary');
          if (summary) summary.textContent = this.partsOpen ? 'Enthaltene Bauteile ausblenden' : 'Alle enthaltenen Bauteile anzeigen';
        }
      }, true);
      this.render();
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node) return {};
      try { return JSON.parse(node.textContent); } catch (error) { return {}; }
    }

    resolve() {
      return rules.resolveConfiguration(this.state, this.catalog);
    }

    money(cents) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency', currency: this.settings.currency || 'EUR'
      }).format(Number(cents) / 100);
    }

    approximateHeight(mm) {
      return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Number(mm) / 1000)} m`;
    }

    useLabel() {
      return ({ 'solid-fuel': 'Festbrennstoff', 'oil-gas': 'Öl / Gas', pellet: 'Pellet' })[this.state.use] || 'Festbrennstoff';
    }

    mountingLabel() {
      return `Wandmontage · ${this.state.wallDistanceMm} mm Wandabstand`;
    }

    routeLabel() {
      return 'Gerade Außenwandführung · freie Höhe bis 2 m';
    }

    customerPartLabel(line) {
      if (line.code === 'DW-JGP01') return 'Jeremias DW-FU Grundpaket';
      if (['DW21', 'DW22', 'DW23', 'DW24'].includes(line.code)) {
        return `Wandhalter für ${this.state.wallDistanceMm} mm Wandabstand`;
      }
      if (['DW01', 'DW02', 'DW49'].includes(line.code)) {
        return `Wandkonsole für ${this.state.wallDistanceMm} mm Wandabstand`;
      }
      return String(line.label || line.code).replace(/\s*\(Einbaumaß\s+[\d.,]+\s*mm\)/gi, '').trim();
    }

    render() {
      const result = this.resolve();
      this.result = result;
      const progress = Math.round(((this.step + 1) / STEPS.length) * 100);
      const price = result.totalPrice === null ? 'Derzeit nicht verfügbar' : this.money(result.totalPrice);

      this.app.innerHTML = `
        <div class="chimney-configurator__head">
          <div class="chimney-configurator__head-system">
            <strong class="chimney-configurator__config-title">Jeremias DW-FU Bausatz</strong>
            <span class="chimney-configurator__diameter">DN ${result.state.dn} · Wandmontage</span>
          </div>
          <div class="chimney-configurator__head-price">
            <strong class="chimney-configurator__live-price" role="status" aria-live="polite">${price}</strong>
            <small>Gesamtpreis inkl. MwSt.</small>
          </div>
        </div>
        <div class="chimney-configurator__progress" aria-label="Schritt ${this.step + 1} von ${STEPS.length}">
          <div class="chimney-configurator__progress-row">
            <strong class="chimney-configurator__progress-name">${STEPS[this.step]}</strong>
            <span class="chimney-configurator__progress-count">Schritt ${this.step + 1} von ${STEPS.length}</span>
          </div>
          <i class="chimney-configurator__progress-track" style="--progress:${progress}%"></i>
        </div>
        <div class="chimney-configurator__workspace">
          <aside class="chimney-configurator__preview" aria-live="polite">${this.preview(result)}</aside>
          <form class="chimney-configurator__panel" data-wizard-form novalidate>
            ${this.renderSystemStep()}
            ${this.renderDiameterStep()}
            ${this.renderHeightStep()}
            ${this.renderMountingStep()}
            ${this.renderRouteStep()}
            ${this.renderSummary(result)}
            <div class="chimney-configurator__navigation">
              ${this.step > 0 ? '<button class="button button--secondary" type="button" data-back>Zurück</button>' : ''}
              ${this.step < 5 ? `<button class="button" type="button" data-next>${this.returnToSummary ? 'Zur Zusammenfassung' : 'Weiter'}</button>` : ''}
            </div>
          </form>
        </div>`;
    }

    renderSystemStep() {
      return `<section class="chimney-configurator__step" data-step="0"${this.step !== 0 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Wofür möchten Sie den Schornstein nutzen?</div>
        <p class="chimney-configurator__step-intro">Wählen Sie die geplante Feuerstätte.</p>
        ${this.choices('use', [
          ['solid-fuel', 'Kaminofen / Festbrennstoff', 'zum Beispiel Holz oder Kohle'],
          ['pellet', 'Pelletofen', 'für Pelletöfen'],
          ['oil-gas', 'Öl / Gas', 'für Öl- oder Gasfeuerstätten']
        ], this.state.use)}
        <p class="chimney-configurator__schematic-note dwfu-selection-note">Die passende Nennweite wählen Sie im nächsten Schritt anhand Ihrer Feuerstätten- oder Schornsteinplanung.</p>
      </section>`;
    }

    renderDiameterStep() {
      return `<section class="chimney-configurator__step" data-step="1"${this.step !== 1 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Welche Nennweite benötigen Sie?</div>
        <p class="chimney-configurator__step-intro">Wählen Sie den Innendurchmesser aus Ihrer Feuerstätten- oder Schornsteinplanung.</p>
        <div class="chimney-configurator__cards chimney-configurator__cards--diameter">
          ${rules.PRIMARY_DNS.map((dn) => this.card({
            name: 'dn', value: dn, selected: this.state.dn === dn,
            title: `DN ${dn}`, hint: `Innen-Ø ${dn} mm`, kind: 'diameter'
          })).join('')}
        </div>
      </section>`;
    }

    renderHeightStep() {
      return `<section class="chimney-configurator__step" data-step="2"${this.step !== 2 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Wie hoch soll Ihr Schornstein werden?</div>
        <p class="chimney-configurator__step-intro">Wählen Sie die gewünschte Schornsteinhöhe. Die passenden Längenelemente stellen wir im Hintergrund zusammen.</p>
        <div class="chimney-configurator__cards chimney-configurator__cards--height">
          ${rules.HEIGHT_RECIPES.map((recipe) => this.card({
            name: 'targetHeightMm', value: recipe.targetMm, selected: this.state.targetHeightMm === recipe.targetMm,
            title: `ca. ${this.approximateHeight(recipe.targetMm)}`,
            kind: 'height'
          })).join('')}
        </div>
      </section>`;
    }

    renderMountingStep() {
      return `<section class="chimney-configurator__step" data-step="3"${this.step !== 3 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Wie soll der Schornstein befestigt werden?</div>
        <p class="chimney-configurator__step-intro">Dieser Konfigurator ist für die Montage an einer tragfähigen Außenwand vorgesehen.</p>
        <div class="dwfu-standard-card" aria-label="Festgelegte Montageart">
          <span class="dwfu-technical-icon dwfu-technical-icon--wall" aria-hidden="true"></span>
          <span><strong>Wandmontage</strong><small>Konsole und Wandhalter passend zum gewählten Abstand.</small></span>
          <span class="dwfu-confirmed-mark" aria-hidden="true">✓</span>
        </div>
        <label class="chimney-configurator__field chimney-configurator__field--spaced">Abstand des Schornsteins zur Außenwand
          <select name="wallDistanceMm" data-state="wallDistanceMm">
            ${rules.WALL_DISTANCES_MM.map((distance) => `<option value="${distance}"${this.state.wallDistanceMm === distance ? ' selected' : ''}>${distance} mm / ${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(distance / 10)} cm</option>`).join('')}
          </select>
        </label>
        <p class="chimney-configurator__schematic-note">Der Befestigungsuntergrund muss für die auftretenden Lasten geeignet sein.</p>
      </section>`;
    }

    renderRouteStep() {
      return `<section class="chimney-configurator__step" data-step="4"${this.step !== 4 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Wie verläuft Ihr Schornstein?</div>
        <p class="chimney-configurator__step-intro">Der Schornstein verläuft gerade an der Außenwand nach oben.</p>
        <div class="dwfu-confirmed-config" aria-label="Festgelegter Gebäudeverlauf">
          <span class="dwfu-confirmed-mark" aria-hidden="true">✓</span>
          <span><strong>Gerade Außenwandführung</strong><small>Der Schornstein verläuft bei dieser Konfiguration gerade entlang der Fassade.</small></span>
        </div>
        <div class="dwfu-route-fact">
          <small>Freie Höhe über dem letzten Wandhalter</small>
          <strong>bis 2 m</strong>
          <span>Für diese Konfiguration sind bis zu 2 m vorgesehen.</span>
        </div>
      </section>`;
    }

    renderSummary(result) {
      if (this.step !== 5) return '<section class="chimney-configurator__step" data-step="5" hidden></section>';
      const allLines = result.bom;
      const total = result.totalPrice === null ? 'Derzeit nicht verfügbar' : this.money(result.totalPrice);
      return `<section class="chimney-configurator__step" data-step="5">
        <div class="chimney-configurator__step-title h3" tabindex="-1">Ihr Jeremias DW-FU Bausatz</div>
        <p class="chimney-configurator__step-intro">Prüfen Sie Ihre Auswahl und legen Sie anschließend den vollständigen Bausatz in den Warenkorb.</p>
        <div class="chimney-configurator__compact-summary">
          ${this.summaryChoice('System', 'Jeremias DW-FU', 0)}
          ${this.summaryChoice('Nennweite', `DN ${result.state.dn}`, 1)}
          ${this.summaryChoice('Höhe', `ca. ${this.approximateHeight(result.state.targetHeightMm)}`, 2)}
          ${this.summaryChoice('Befestigung', this.mountingLabel(), 3)}
          ${this.summaryChoice('Verlauf', this.routeLabel(), 4)}
          ${this.summaryChoice('Einsatz', this.useLabel(), 0)}
        </div>
        <div class="chimney-configurator__summary-total">
          <small>Gesamtpreis</small><strong>${total}</strong><p>inklusive aller enthaltenen Komponenten</p>
        </div>
        <p class="dwfu-delivery"><span aria-hidden="true">✓</span><span><strong>Lieferzeit: ca. 1–2 Wochen</strong><small>Für den vollständigen Bausatz</small></span></p>
        <details class="chimney-configurator__parts"${this.partsOpen ? ' open' : ''}>
          <summary>${this.partsOpen ? 'Enthaltene Bauteile ausblenden' : 'Alle enthaltenen Bauteile anzeigen'}</summary>
          <ul class="dwfu-parts-list">${allLines.map((line) => `<li><strong>${line.quantity} × ${escapeHtml(this.customerPartLabel(line))}</strong><em>${this.money(line.price * line.quantity)}</em></li>`).join('')}</ul>
        </details>
        <p class="dwfu-advisory">Hinweis: Bitte stimmen Sie die Ausführung vor der Montage mit Ihrem zuständigen Schornsteinfeger ab und stellen Sie sicher, dass Befestigungsuntergrund und Einbausituation geeignet sind.</p>
        <button class="button button--full-width" type="button" data-add-configuration${result.cartEligible || this.submitting ? (this.submitting ? ' disabled' : '') : ' disabled'}>${this.submitting ? 'Wird hinzugefügt …' : 'Bausatz in den Warenkorb'}</button>
        <p class="chimney-configurator__cart-note">Alle Bestandteile werden gemeinsam als eine Konfiguration in den Warenkorb gelegt.</p>
        ${this.cartMessage ? `<div class="chimney-configurator__error" role="alert">${escapeHtml(this.cartMessage)}</div>` : ''}
      </section>`;
    }

    card({ name, value, selected, title, hint, kind }) {
      return `<label class="chimney-configurator__card chimney-configurator__card--compact chimney-configurator__card--${kind}${selected ? ' is-selected' : ''}">
        <input type="radio" name="${name}" data-state="${name}" value="${escapeHtml(value)}"${selected ? ' checked' : ''}>
        <strong>${escapeHtml(title)}</strong>${hint ? `<small>${escapeHtml(hint)}</small>` : ''}
      </label>`;
    }

    choices(name, entries, value) {
      return `<div class="chimney-configurator__choices chimney-configurator__choices--use">${entries.map(([key, label, hint]) => `<label class="chimney-configurator__choice${String(value) === String(key) ? ' is-selected' : ''}">
        <input type="radio" name="${name}" data-state="${name}" value="${escapeHtml(key)}"${String(value) === String(key) ? ' checked' : ''}>
        <strong>${escapeHtml(label)}</strong><small>${escapeHtml(hint)}</small>
      </label>`).join('')}</div>`;
    }

    summaryChoice(label, value, step) {
      return `<span><small>${escapeHtml(label)}</small><strong>${escapeHtml(value)}</strong><button type="button" data-edit-step="${step}" aria-label="${escapeHtml(label)} ändern">Ändern</button></span>`;
    }

    preview(result) {
      const totalHeight = result.state.targetHeightMm;
      const pipeBottom = 442;
      const supportStrokeWidth = 7;
      const supportBottomY = pipeBottom - supportStrokeWidth / 2;
      const pipeTop = Math.max(48, 215 - ((totalHeight - 2209) / (8817 - 2209)) * 150);
      const wallX = 176;
      const distance = Math.max(40, Math.min(88, 34 + result.state.wallDistanceMm / 7));
      const pipeWidth = 34;
      const pipeX = wallX - distance - pipeWidth;
      const pipeCenter = pipeX + pipeWidth / 2;
      const holderCount = Math.max(1, Math.min(5, result.holderCount || 1));
      const topHolderY = Math.min(368, pipeTop + (result.state.freeHeightM * 1000 / totalHeight) * (pipeBottom - pipeTop));
      const holders = Array.from({ length: holderCount }, (_, index) => {
        const y = holderCount === 1 ? 368 : 368 - (index / (holderCount - 1)) * (368 - topHolderY);
        return `<g aria-hidden="true"><line x1="${pipeX + pipeWidth}" y1="${y}" x2="${wallX}" y2="${y}" stroke="#6f7474" stroke-width="4"/><rect x="${wallX - 3}" y="${y - 7}" width="6" height="14" class="dwfu-preview-bracket"/><circle cx="${pipeCenter}" cy="${y}" r="19" fill="none" stroke="#858a8a" stroke-width="2"/></g>`;
      }).join('');
      const segments = Array.from({ length: Math.max(3, Math.min(9, Math.round(totalHeight / 1000))) }, (_, index) => {
        const y = pipeBottom - ((pipeBottom - pipeTop) / Math.max(3, Math.min(9, Math.round(totalHeight / 1000)))) * (index + 1);
        return `<line x1="${pipeX - 2}" y1="${y}" x2="${pipeX + pipeWidth + 2}" y2="${y}" stroke="#737778" stroke-width="2"/>`;
      }).join('');
      const support = `<path d="M ${pipeX + pipeWidth} 425 H ${wallX} V ${supportBottomY} H ${pipeX + pipeWidth}" class="chimney-configurator__preview-support" fill="none" stroke="#646969" stroke-width="${supportStrokeWidth}"/><rect x="${wallX - 5}" y="414" width="10" height="38" class="dwfu-preview-bracket"/>`;

      return `<div class="chimney-configurator__preview-stage">
        <p class="chimney-configurator__preview-label">Vorschau</p>
        <svg viewBox="0 0 360 520" role="img" aria-label="Schematische Vorschau eines Jeremias DW-FU Edelstahlschornsteins">
          <path d="M ${wallX} 94 H 356 V 468 H ${wallX} Z" class="dwfu-preview-building"/>
          <rect x="226" y="142" width="72" height="74" class="dwfu-preview-window"/><line x1="262" y1="142" x2="262" y2="216" stroke="#c9c9c5" stroke-width="2"/><line x1="226" y1="179" x2="298" y2="179" stroke="#c9c9c5" stroke-width="2"/>
          <rect x="226" y="282" width="72" height="74" class="dwfu-preview-window"/><line x1="262" y1="282" x2="262" y2="356" stroke="#c9c9c5" stroke-width="2"/><line x1="226" y1="319" x2="298" y2="319" stroke="#c9c9c5" stroke-width="2"/>
          ${holders}${support}
          <rect x="${pipeX}" y="${pipeTop}" width="${pipeWidth}" height="${pipeBottom - pipeTop}" class="dwfu-preview-steel"/>
          <rect x="${pipeX + 7}" y="${pipeTop + 3}" width="5" height="${pipeBottom - pipeTop - 6}" class="dwfu-preview-highlight"/>
          ${segments}
          <path d="M ${pipeX + pipeWidth} 395 H ${wallX + 25}" fill="none" stroke="#707475" stroke-width="28"/><path d="M ${pipeX + pipeWidth} 395 H ${wallX + 25}" fill="none" stroke="#d8d9d8" stroke-width="23"/>
          <rect x="${pipeX + 5}" y="407" width="24" height="33" rx="2" class="dwfu-preview-steel"/>
          <path d="M ${pipeX - 7} ${pipeTop} H ${pipeX + pipeWidth + 7} L ${pipeX + pipeWidth + 1} ${pipeTop - 16} H ${pipeX - 1} Z" class="dwfu-preview-steel"/>
          <line x1="42" y1="${pipeTop}" x2="42" y2="${pipeBottom}" class="dwfu-preview-measure"/><path d="M 37 ${pipeTop + 8} L 42 ${pipeTop} L 47 ${pipeTop + 8} M 37 ${pipeBottom - 8} L 42 ${pipeBottom} L 47 ${pipeBottom - 8}" class="dwfu-preview-measure"/><text x="34" y="${(pipeTop + pipeBottom) / 2}" class="dwfu-preview-measure-text" transform="rotate(-90 34 ${(pipeTop + pipeBottom) / 2})">ca. ${this.approximateHeight(totalHeight)}</text>
          <line x1="${pipeX + pipeWidth}" y1="478" x2="${wallX}" y2="478" class="dwfu-preview-distance"/><path d="M ${pipeX + pipeWidth + 5} 474 L ${pipeX + pipeWidth} 478 L ${pipeX + pipeWidth + 5} 482 M ${wallX - 5} 474 L ${wallX} 478 L ${wallX - 5} 482" class="dwfu-preview-distance"/><text x="${(pipeX + pipeWidth + wallX) / 2}" y="497" class="dwfu-preview-distance-text">${result.state.wallDistanceMm} mm</text>
        </svg>
      </div>
      <div class="chimney-configurator__preview-meta">
        <span><small>Höhe</small><strong>ca. ${this.approximateHeight(totalHeight)}</strong></span>
        <span><small>Nennweite</small><strong>DN ${result.state.dn}</strong></span>
        <span><small>Wandabstand</small><strong>${result.state.wallDistanceMm} mm</strong></span>
      </div>`;
    }

    onChange(event) {
      const input = event.target;
      const stateKey = input.dataset.state;
      if (!stateKey) return;
      const numericFields = ['dn', 'targetHeightMm', 'wallDistanceMm', 'freeHeightM'];
      this.state[stateKey] = numericFields.includes(stateKey) ? asNumber(input.value) : input.value;
      this.cartMessage = '';
      this.render();
    }

    async onClick(event) {
      if (event.target.closest('[data-next]')) {
        this.step = this.returnToSummary ? 5 : Math.min(5, this.step + 1);
        this.returnToSummary = false;
        this.render();
        this.focusStep();
        return;
      }
      if (event.target.closest('[data-back]')) {
        this.step = Math.max(0, this.step - 1);
        this.render();
        this.focusStep();
        return;
      }
      const edit = event.target.closest('[data-edit-step]');
      if (edit) {
        this.step = Math.max(0, Math.min(4, Number(edit.dataset.editStep)));
        this.returnToSummary = true;
        this.render();
        this.focusStep();
        return;
      }
      if (event.target.closest('[data-add-configuration]')) await this.addToCart();
    }

    focusStep() {
      requestAnimationFrame(() => this.querySelector('.chimney-configurator__step:not([hidden]) .chimney-configurator__step-title')?.focus?.());
    }

    async addToCart() {
      const result = this.resolve();
      if (!result.cartEligible || this.submitting) return;
      this.submitting = true;
      this.cartMessage = '';
      this.render();
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(rules.buildCartPayload(result))
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.description || 'Die Konfiguration konnte nicht in den Warenkorb gelegt werden.');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'jeremias-dw-fu-configurator' } }));
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        this.submitting = false;
        this.cartMessage = error.message || 'Die Konfiguration konnte nicht in den Warenkorb gelegt werden.';
        this.render();
      }
    }
  }

  if (!customElements.get('jeremias-dw-fu-configurator')) {
    customElements.define('jeremias-dw-fu-configurator', JeremiasDwFuConfigurator);
  }
})();
